import { yupResolver } from '@hookform/resolvers/yup';
import { Loader2 } from 'lucide-react';
import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Button } from '@/Components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/Components/shadcn/dialog';
import { Field, FieldError, FieldLabel } from '@/Components/shadcn/field';
import { Input } from '@/Components/shadcn/input';
import { Textarea } from '@/Components/shadcn/textarea';
import { TypographyP } from '@/Components/shadcn/typography';
import { criteriaScoreFieldNames, CriteriaScoreFormValues, criteriaScoreSchema } from '../CriteriaScore';
import UIStrings from '@/Utils/UIStrings';
import { ExperienceCriterionFinalScore } from '@/Models/ExperienceCriterionFinalScore';

interface CriteriaScoreFormDialogProps {
    open: boolean;
    criteriaFinalScore: ExperienceCriterionFinalScore;
    onClose: () => void;
    onSubmit: (form: CriteriaScoreFormValues, criterionScore: ExperienceCriterionFinalScore) => Promise<void>;
}

const CriteriaScoreFormDialog = ({ criteriaFinalScore, open, onClose, onSubmit }: CriteriaScoreFormDialogProps) => {
    const form = useForm<CriteriaScoreFormValues>({
        resolver: yupResolver(criteriaScoreSchema),
        mode: 'all',
    });
    const isFormEqualOnSubmit = !form.formState.isDirty && form.formState.isSubmitted;

    useEffect(() => {
        if (open && criteriaFinalScore) {
            form.reset({
                userDefinedScore: criteriaFinalScore.criterionScore?.score,
                userDefinedJustification: criteriaFinalScore.justification ?? undefined,
            });
        }
    }, [open, criteriaFinalScore, form]);

    const handleSubmit = async (formValues: CriteriaScoreFormValues) => {
        if (form.formState.isDirty) {
            await onSubmit(formValues, criteriaFinalScore);
        }
    };

    return (
        <FormProvider {...form}>
            <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
                <DialogContent className="max-w-xl" showCloseButton>
                    <DialogHeader>
                        <DialogTitle>{UIStrings.NewJustificationAndScore}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
                        <div className="my-6 flex flex-col gap-8 py-2">
                            <Controller
                                name={criteriaScoreFieldNames.userDefinedScore}
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <Field data-invalid={fieldState.error ? true : undefined} className="w-full">
                                        <FieldLabel htmlFor="user-defined-score">{UIStrings.NewScore}</FieldLabel>
                                        <NumericFormat
                                            customInput={Input}
                                            getInputRef={field.ref}
                                            thousandSeparator
                                            allowNegative={false}
                                            id="user-defined-score"
                                            name={field.name}
                                            value={field.value === undefined || field.value === null ? '' : field.value}
                                            onValueChange={(values) => {
                                                field.onChange(
                                                    values.floatValue === undefined ? undefined : values.floatValue,
                                                );
                                            }}
                                            onBlur={field.onBlur}
                                            aria-invalid={fieldState.error ? true : undefined}
                                        />
                                        <FieldError>{fieldState.error?.message}</FieldError>
                                    </Field>
                                )}
                            />
                            <Controller
                                name={criteriaScoreFieldNames.userDefinedJustification}
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <Field data-invalid={fieldState.error ? true : undefined} className="w-full">
                                        <FieldLabel htmlFor="user-defined-justification">
                                            {UIStrings.NewJustification}
                                        </FieldLabel>
                                        <Textarea
                                            id="user-defined-justification"
                                            rows={6}
                                            className="field-sizing-fixed resize-none"
                                            {...field}
                                            value={field.value ?? ''}
                                            aria-invalid={fieldState.error ? true : undefined}
                                        />
                                        <FieldError>{fieldState.error?.message}</FieldError>
                                    </Field>
                                )}
                            />
                            {isFormEqualOnSubmit && (
                                <TypographyP className="text-destructive">
                                    {UIStrings.PleaseUpdateScoreAndJustificationToProceed}
                                </TypographyP>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="submit" size="lg" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? (
                                    <Loader2 className="mr-2 size-4 shrink-0 animate-spin" aria-hidden />
                                ) : null}
                                {UIStrings.Update}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </FormProvider>
    );
};

export default CriteriaScoreFormDialog;
